import { BASEMAP_COARSEST_LEVEL, BASEMAP_FINEST_LEVEL, BASEMAP_TILE_PIXELS } from "./manifest";
import {
  emptyTile,
  NO_NUMBER,
  parentOf,
  placeKey,
  type RasterType,
  type TilePixels,
  type TilePlace,
  tilesAcross,
} from "./tiles";

/**
 * A raster's tiles as a quadtree. Its leaves cover the world exactly, each holding a tile's pixels at
 * its own level: the base level wherever nobody changed the detail, deeper where someone made it
 * finer. A place above a leaf is never edited; its pixels are always made again from its children.
 */
export class LeafTree {
  /** Places at the base level or deeper whose four children are stored instead of them. */
  readonly #splits = new Set<string>();

  constructor(
    readonly base: number,
    readonly faces: number,
    splits: Iterable<TilePlace> = [],
  ) {
    for (const place of splits) this.#splits.add(placeKey(place));
  }

  /** Whether a place is a leaf: at the base level or under a split, and not split itself. */
  isLeaf(place: TilePlace): boolean {
    if (place.level < this.base || this.#splits.has(placeKey(place))) return false;
    return place.level === this.base || this.#splits.has(placeKey(parentOf(place)));
  }

  isSplit(place: TilePlace): boolean {
    return this.#splits.has(placeKey(place));
  }

  /** The leaf holding a point on a face, its position as s and t from 0 to 1, t upward. */
  leafAt(face: number, s: number, t: number): TilePlace {
    let place = placeContaining(face, this.base, s, t);
    while (this.#splits.has(placeKey(place))) place = placeContaining(face, place.level + 1, s, t);
    return place;
  }

  /** Every leaf at or under a place, or under every face's root without one. */
  leaves(under?: TilePlace): TilePlace[] {
    const roots = under ? [under] : faceRoots(this.faces);
    const found: TilePlace[] = [];
    const visit = (place: TilePlace) => {
      if (place.level < this.base || this.#splits.has(placeKey(place))) {
        for (const child of childrenOf(place)) visit(child);
      } else found.push(place);
    };
    for (const root of roots) {
      if (root.level > this.base && !this.isLeaf(root) && !this.#splits.has(placeKey(root))) {
        found.push(this.leafAt(root.face, ...middleOf(root)));
      } else visit(root);
    }
    return found;
  }

  /** The deepest level any leaf sits at. */
  get deepest(): number {
    let deepest = this.base;
    for (const key of this.#splits) deepest = Math.max(deepest, levelOfKey(key) + 1);
    return deepest;
  }

  /** The places split, for storing. */
  splits(): TilePlace[] {
    return [...this.#splits].map(placeOfKey);
  }

  /** Splits a leaf into its four children. */
  split(place: TilePlace): void {
    if (!this.isLeaf(place)) throw new Error(`${placeKey(place)} isn't a leaf.`);
    if (place.level >= BASEMAP_FINEST_LEVEL) throw new Error("A tile can't be any finer.");
    this.#splits.add(placeKey(place));
  }

  /**
   * Makes a place a leaf again, dropping every split under it. Returns the leaves it replaces, which
   * the caller merges into its pixels.
   */
  merge(place: TilePlace): TilePlace[] {
    if (place.level < this.base) throw new Error("A tile can't be coarser than the base level.");
    if (this.isLeaf(place)) return [];
    const replaced = this.leaves(place);
    for (const key of [...this.#splits]) {
      if (isAtOrUnder(placeOfKey(key), place)) this.#splits.delete(key);
    }
    return replaced;
  }

  copy(): LeafTree {
    return new LeafTree(this.base, this.faces, this.splits());
  }
}

/** The four places one level finer, in reading order: top left, top right, bottom left, bottom right. */
export function childrenOf({ face, level, x, y }: TilePlace): TilePlace[] {
  return [
    { face, level: level + 1, x: 2 * x, y: 2 * y },
    { face, level: level + 1, x: 2 * x + 1, y: 2 * y },
    { face, level: level + 1, x: 2 * x, y: 2 * y + 1 },
    { face, level: level + 1, x: 2 * x + 1, y: 2 * y + 1 },
  ];
}

/** Whether a place is the other or lies under it. */
export function isAtOrUnder(place: TilePlace, other: TilePlace): boolean {
  if (place.face !== other.face || place.level < other.level) return false;
  const shift = place.level - other.level;
  return (
    Math.floor(place.x / 2 ** shift) === other.x && Math.floor(place.y / 2 ** shift) === other.y
  );
}

/** The place at a level holding a point on a face, s and t from 0 to 1 with t upward. */
export function placeContaining(face: number, level: number, s: number, t: number): TilePlace {
  const across = tilesAcross(level);
  const clamp = (value: number) => Math.min(across - 1, Math.max(0, Math.floor(value * across)));
  return { face, level, x: clamp(s), y: clamp(1 - t) };
}

const faceRoots = (faces: number): TilePlace[] =>
  Array.from({ length: faces }, (_, face) => ({ face, level: BASEMAP_COARSEST_LEVEL, x: 0, y: 0 }));

const middleOf = ({ level, x, y }: TilePlace): [number, number] => {
  const across = tilesAcross(level);
  return [(x + 0.5) / across, 1 - (y + 0.5) / across];
};

const levelOfKey = (key: string) => Number(key.split("/")[1]);
const placeOfKey = (key: string): TilePlace => {
  const [face, level, x, y] = key.split("/").map(Number) as [number, number, number, number];
  return { face, level, x, y };
};

/**
 * One child's pixels, from its parent's quarter made twice as large. Categories, and numbers held in
 * steps, keep each parent pixel's value in the four it becomes; heights and colors blend between
 * neighbors, so a finer place looks as it did.
 */
export function splitTile(
  type: RasterType,
  parent: TilePixels,
  quadrant: number,
  smooth: boolean,
): TilePixels {
  const child = emptyTile(type);
  const size = BASEMAP_TILE_PIXELS;
  const half = size / 2;
  const offsetX = (quadrant % 2) * half;
  const offsetY = Math.floor(quadrant / 2) * half;
  const channels = type === "color" ? 4 : 1;
  const at = (x: number, y: number) =>
    (Math.min(size - 1, Math.max(0, y)) * size + Math.min(size - 1, Math.max(0, x))) * channels;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const target = (y * size + x) * channels;
      // Where this child pixel's middle falls in the parent, in parent pixels.
      const px = offsetX + (x + 0.5) / 2 - 0.5;
      const py = offsetY + (y + 0.5) / 2 - 0.5;
      const nearest = at(offsetX + (x >> 1), offsetY + (y >> 1));
      if (!smooth || type === "category") {
        for (let channel = 0; channel < channels; channel++) {
          child[target + channel] = parent[nearest + channel] ?? 0;
        }
        continue;
      }
      const x0 = Math.floor(px);
      const y0 = Math.floor(py);
      const fx = px - x0;
      const fy = py - y0;
      const corners = [at(x0, y0), at(x0 + 1, y0), at(x0, y0 + 1), at(x0 + 1, y0 + 1)];
      const weights = [(1 - fx) * (1 - fy), fx * (1 - fy), (1 - fx) * fy, fx * fy];
      if (channels === 1 && corners.some(index => parent[index] === NO_NUMBER)) {
        child[target] = parent[nearest] ?? NO_NUMBER;
        continue;
      }
      for (let channel = 0; channel < channels; channel++) {
        let sum = 0;
        for (let corner = 0; corner < 4; corner++) {
          sum += (parent[(corners[corner] ?? 0) + channel] ?? 0) * (weights[corner] ?? 0);
        }
        child[target + channel] = Math.round(sum);
      }
    }
  }
  return child;
}

/**
 * A tree's shape as its manifest's detail map: from each face's root, one bit a place in pre-order,
 * most significant first, base64url. Above the base level a 1 says detail goes deeper than the base
 * somewhere beneath and a 0 that it doesn't, so nothing beneath is written. From the base level down a
 * 1 says the place is split. A tree with no detail past its base is the empty string.
 */
export function encodeDetailMap(tree: LeafTree): string {
  const splits = tree.splits();
  if (splits.length === 0) return "";
  const deeper = new Set<string>();
  for (const split of splits) {
    for (let place = split; place.level >= BASEMAP_COARSEST_LEVEL; place = parentOf(place)) {
      deeper.add(placeKey(place));
    }
  }
  const bits: number[] = [];
  const visit = (place: TilePlace) => {
    const on = deeper.has(placeKey(place));
    bits.push(on ? 1 : 0);
    if (on) for (const child of childrenOf(place)) visit(child);
  };
  for (const root of faceRoots(tree.faces)) visit(root);
  const bytes = new Uint8Array(Math.ceil(bits.length / 8));
  bits.forEach((bit, index) => {
    if (bit) bytes[index >> 3] = (bytes[index >> 3] ?? 0) | (0x80 >> (index & 7));
  });
  return toBase64Url(bytes);
}

export function decodeDetailMap(detailMap: string, base: number, faces: number): LeafTree {
  if (!detailMap) return new LeafTree(base, faces);
  const bytes = fromBase64Url(detailMap);
  let index = 0;
  const read = () => {
    const bit = ((bytes[index >> 3] ?? 0) >> (7 - (index & 7))) & 1;
    index++;
    return bit;
  };
  const splits: TilePlace[] = [];
  const visit = (place: TilePlace) => {
    if (!read()) return;
    if (place.level >= base) splits.push(place);
    for (const child of childrenOf(place)) visit(child);
  };
  for (const root of faceRoots(faces)) visit(root);
  return new LeafTree(base, faces, splits);
}

function toBase64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/, "");
}

function fromBase64Url(text: string): Uint8Array {
  const binary = atob(text.replaceAll("-", "+").replaceAll("_", "/"));
  return Uint8Array.from(binary, character => character.charCodeAt(0));
}
